import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get authorization code from URL
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          // Handle authentication errors
          console.error('Authentication error:', error, errorDescription);
          
          if (error === 'access_denied') {
            setError('Authentication was cancelled. Please try again.');
          } else {
            setError(`Authentication failed: ${errorDescription || error}`);
          }
          setIsProcessing(false);
          return;
        }

        // Check if we have tokens in URL fragment (implicit flow) or code in query params
        const fragment = window.location.hash.substring(1);
        const fragmentParams = new URLSearchParams(fragment);
        const hasTokensInFragment = fragmentParams.get('id_token') || fragmentParams.get('access_token');

        if (!code && !hasTokensInFragment) {
          setError('No authorization code or tokens received.');
          setIsProcessing(false);
          return;
        }

        console.log('🔄 Processing authentication callback...');
        
        // Determine which authentication flow we're handling based on stored session data
        const authAttempt = sessionStorage.getItem('auth_attempt');
        console.log('🔍 Auth attempt type:', authAttempt);

        // If we have tokens in fragment, it's always Azure AD implicit flow
        if (hasTokensInFragment || authAttempt === 'azure_ad_direct') {
          // Handle Azure AD direct authentication (staff/sales) - Implicit Flow
          console.log('🔄 Processing Azure AD authentication (implicit flow)...');
          
          // Get tokens from the URL fragment (already parsed above)
          const id_token = fragmentParams.get('id_token');
          const access_token = fragmentParams.get('access_token');

          if (!id_token) {
            throw new Error('No ID token received from Azure AD implicit flow');
          }

          // Parse the ID token to get user info
          const tokenPayload = JSON.parse(atob(id_token.split('.')[1]));
          
          const userInfo = {
            id: tokenPayload.sub || tokenPayload.oid,
            name: tokenPayload.name || tokenPayload.given_name,
            email: tokenPayload.email || tokenPayload.preferred_username,
            userType: 'staff',
            // isAdmin is intentionally not derived from email here. The
            // authoritative value comes from /api/v1/user/info on the backend.
            isAdmin: false
          };

          // Store authentication data
          localStorage.setItem('chime_token', id_token);
          if (access_token) {
            localStorage.setItem('chime_access_token', access_token);
          }
          localStorage.setItem('chime_user', JSON.stringify(userInfo));
          
          // Set token expiry (1 hour from now)
          const expiryTime = Date.now() + (60 * 60 * 1000);
          localStorage.setItem('chime_token_expires_at', expiryTime.toString());
          
          console.log('✅ Azure AD authentication successful (implicit flow):', userInfo.email);
          
        } else if (authAttempt === 'b2c_unified') {
          // Handle Azure B2C unified authentication (surgeons + backup for staff)
          console.log('🔄 Processing Azure B2C unified authentication...');
          
          const tokenUrl = 'https://chimeplatform.b2clogin.com/chimeplatform.onmicrosoft.com/oauth2/v2.0/token?p=B2C_1A_UnifiedSignIn';
          
          const tokenData = new URLSearchParams({
            client_id: '6049c0d3-9e0d-46f3-ab92-27115545d6ea',
            grant_type: 'authorization_code',
            code: code || '',
            redirect_uri: window.location.origin + '/auth/callback',
            scope: 'openid profile email'
          });

          const tokenResponse = await fetch(tokenUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: tokenData
          });

          const tokenResult = await tokenResponse.json();

          if (tokenResult.error) {
            console.error('B2C token exchange failed:', tokenResult.error_description);
            setError(`B2C authentication failed: ${tokenResult.error_description}`);
            setIsProcessing(false);
            return;
          }

          const { id_token } = tokenResult;

          if (!id_token) {
            throw new Error('No ID token received from B2C');
          }

          // Parse the ID token to get user info
          const tokenPayload = JSON.parse(atob(id_token.split('.')[1]));
          
          const userInfo = {
            id: tokenPayload.sub || tokenPayload.oid,
            name: tokenPayload.name || tokenPayload.given_name || tokenPayload.displayName,
            email: tokenPayload.email || tokenPayload.emails?.[0],
            userType: tokenPayload.userType || 'surgeon',
            isAdmin: false
          };

          // Store authentication data
          localStorage.setItem('chime_token', id_token);
          localStorage.setItem('chime_user', JSON.stringify(userInfo));
          
          // Set token expiry (1 hour from now)
          const expiryTime = Date.now() + (60 * 60 * 1000);
          localStorage.setItem('chime_token_expires_at', expiryTime.toString());
          
          console.log('✅ B2C unified authentication successful:', userInfo.email);
          
        } else if (code) {
          // Handle unified backend authentication 
          console.log('🔄 Processing unified backend authentication...');
          
          // Exchange the unified code for token data
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/unified/process`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code })
          });

          if (!response.ok) {
            throw new Error('Failed to process unified authentication');
          }

          const tokenData = await response.json();
          
          const userInfo = {
            id: tokenData.user.id,
            name: `${tokenData.user.firstName} ${tokenData.user.lastName}`.trim(),
            email: tokenData.user.email,
            userType: tokenData.user.userType,
            // Trust the backend's isAdmin flag; do not derive from email domain.
            isAdmin: tokenData.user.isAdmin === true
          };

          // Store authentication data
          localStorage.setItem('chime_token', tokenData.access_token);
          localStorage.setItem('chime_user', JSON.stringify(userInfo));
          
          // Set token expiry (1 hour from now)
          const expiryTime = Date.now() + (60 * 60 * 1000);
          localStorage.setItem('chime_token_expires_at', expiryTime.toString());
          
          console.log('✅ Unified backend authentication successful:', userInfo.email);
          
        } else {
          // Unknown auth attempt type
          console.error('Unknown auth attempt type:', authAttempt);
          setError('Unknown authentication flow. Please try again.');
          setIsProcessing(false);
          return;
        }
        
        // Clear session storage
        sessionStorage.removeItem('auth_attempt');
        sessionStorage.removeItem('login_email');
        
        // Redirect to main app
        window.location.href = '/';
      } catch (err) {
        console.error('Error processing authentication callback:', err);
        setError('Failed to process authentication response.');
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [navigate]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Authentication...</h2>
          <p className="text-gray-600">Please wait while we complete your login.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Error</h3>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;